import React, { useEffect, useMemo, useState } from "react";
import { Table, Tag, DatePicker } from "antd";
import { IconPlus } from "@tabler/icons-react";
import CustomBtn from "../../components/btn/CustomBtn";
import CustomSelect from "../../components/select/CustomSelect";
import PageHeader from "../../components/header/PageHeader";
import ActionDrawer from "./components/ActionDrawer";
import api from "../../api";
import { useToast } from "../../components/toast";
import styles from "./schedule.module.scss";
import DetailDrawer from "./components/DetailDrawer";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

export default function Schedule() {
  const [list, setList] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);

  // ====== state ======
  const [showDrawer, setShowDrawer] = useState(false);
  const [visible, setVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);

  // form state
  const [name, setName] = useState("");
  const [date, setDate] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [room, setRoom] = useState(null);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });

  const toast = useToast();

  const buildTableData = (items = []) => {
    const rows = [];

    // 1) Өдрөөр бүлэглэх
    const grouped = items.reduce((acc, s) => {
      const dayKey = dayjs(s.starts_at).format("YYYY-MM-DD");
      if (!acc[dayKey]) acc[dayKey] = [];
      acc[dayKey].push(s);
      return acc;
    }, {});

    // 2) Өдөр бүрийн дотор цагийн дарааллаар эрэмбэлээд rowSpan тооцоолно
    Object.keys(grouped)
      .sort() // YYYY-MM-DD өсөх эрэмбэ
      .forEach((dayKey) => {
        const slots = grouped[dayKey].sort(
          (a, b) => new Date(a.starts_at) - new Date(b.starts_at),
        );

        slots.forEach((s, index) => {
          rows.push({
            ...s,
            // Өдөр багана дээр ашиглах rowSpan
            _dateRowSpan: index === 0 ? slots.length : 0,
          });
        });
      });

    return rows;
  };

  // Drawer нээгдэхэд классуудыг татах
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get_classes();
        setClasses(res?.data?.data || []);
        setRoom(res?.data?.data?.[0]?.id);
      } catch (e) {
        toast("Class жагсаалт татах үед алдаа гарлаа.", "error");
      }
    })();
  }, []);

  const fetchSchedules = async (page = 1, limit = 10, range = dateRange) => {
    if (!room) {
      setList([]);
      setPagination((prev) => ({ ...prev, page: 1, total: 0 }));
      return;
    }

    setLoading(true);

    let from;
    let to;
    if (range && range.length === 2 && range[0] && range[1]) {
      from = range[0].startOf("day").toISOString();
      to = range[1].endOf("day").toISOString();
    }

    const res = await api.get_schedules({
      class_id: room,
      page,
      limit,
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
    });

    if (res.status === 200) {
      const { items, pagination: meta } = res?.data?.data || {};

      // console.log("items --->", items);
      const tableRows = buildTableData(items || []);

      // console.log("buildTableData --->", tableRows);
      setList(tableRows);
      setPagination({
        page: meta?.page || page,
        limit: meta?.limit || limit,
        total: meta?.total || 0,
      });
    } else {
      toast(res?.data?.message || "Хуваарь татахад алдаа гарлаа", "error");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!room) {
      setList([]);
      setPagination({ page: 1, limit: 10, total: 0 });
      return;
    }
    fetchSchedules(1, pagination.limit, dateRange);
  }, [room, dateRange]);

  // ====== helpers ======
  const resetForm = () => {
    setName("");
    setDate(null);
    // setType(null);
    setEditingId(null);
  };

  const openCreate = () => {
    resetForm();
    setShowDrawer(true);
  };

  // ====== table ======
  const columns = useMemo(
    () => [
      {
        title: "Өдөр",
        dataIndex: "starts_at",
        width: 80,
        render: (val, row) =>
          row._isEmpty || !val ? "-" : dayjs(val).format("YYYY-MM-DD"),
        onCell: (record) => ({
          rowSpan: record._dateRowSpan ?? 0, // өдөр rowSpan
        }),
      },
      {
        title: "Цаг",
        key: "time",
        width: 160,
        render: (_, row) => {
          if (row._isEmpty || !row.starts_at || !row.ends_at) return "-";
          return (
            <>
              {dayjs(row.starts_at).format("HH:mm")} -{" "}
              {dayjs(row.ends_at).format("HH:mm")}
            </>
          );
        },
      },
      {
        title: "Дүүргэлт",
        key: "capacity",
        width: 140,
        render: (_, row) => {
          if (row._isEmpty || row.capacity == null) return "-";
          const isFull = row.booked_count >= row.capacity;
          return (
            <>
              <Tag color={isFull ? "red" : "green"}>
                {row.booked_count} / {row.capacity}
              </Tag>
              {row.is_closed && <Tag color="default">Хаагдсан</Tag>}
            </>
          );
        },
      },
    ],
    [],
  );

  const openDetail = (row) => {
    if (!row || row._isEmpty) return;
    setEditingRecord(row);
    setVisible(true);
  };

  return (
    <div className="d-flex flex-column pb-5">
      <PageHeader
        title={"Хуваарь"}
        right={
          <CustomBtn
            title="Хуваарь үүсгэх"
            icon={<IconPlus size={18} />}
            onClick={openCreate}
          />
        }
      />

      <div className="d-flex gap-2 mb-3">
        <CustomSelect
          allowClear
          showSearch
          required
          placeholder="Өрөө сонгох"
          value={room}
          onChange={(e) => setRoom(e)}
          options={classes.map((c) => ({
            value: c.id,
            label: `${c.title} (${c.program})`,
          }))}
          style={{ width: 250 }}
        />
        <RangePicker
          placeholder={["Эхлэх өдөр", "Дуусах өдөр"]}
          value={dateRange}
          onChange={(vals) => {
            setDateRange(vals);
            // range солигдоход шууд 1-р хуудсаас дахин ачаалах
            fetchSchedules(1, pagination.limit, vals);
          }}
          style={{ width: 260 }}
          format="YYYY-MM-DD"
        />
      </div>

      {room && (
        <div className="d-flex gap-2">
          <div className={styles.summary}>
            <div className={styles.label}>Program:</div>
            <Tag color="green">
              {classes.find((e) => e.id === room).program}
            </Tag>
          </div>

          <div className={styles.summary}>
            <div className={styles.label}>Өрөө:</div>
            <div>{classes.find((e) => e.id === room).title}</div>
          </div>
        </div>
      )}

      <Table
        rowKey="id"
        bordered
        size="small"
        rowClassName={(row) => (row.is_closed ? styles.closedRow : "")}
        columns={columns}
        dataSource={list}
        loading={loading}
        pagination={{
          current: pagination.page,
          pageSize: pagination.limit,
          total: pagination.total,
          showSizeChanger: true,
          onChange: (page, pageSize) => {
            fetchSchedules(page, pageSize);
          },
          onShowSizeChange: (page, pageSize) => {
            fetchSchedules(1, pageSize);
          },
        }}
        onRow={(record) => ({
          onClick: () => openDetail(record),
        })}
      />

      <ActionDrawer
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
        record={editingId}
        classes={classes}
        trigger={() => fetchSchedules(1, pagination.limit)}
      />

      <DetailDrawer
        open={visible}
        onClose={() => setVisible(false)}
        record={editingRecord}
        trigger={() => fetchSchedules(pagination.page, pagination.limit)}
      />
    </div>
  );
}
